import type {
  AdminUsageDashboardQuery,
  AdminUsageDashboardResponse,
  UsageByKeyResponse,
  UsageDailyResponse,
  UsageHourlyResponse,
  UsageRequestLogsResponse,
  UsageSummaryResponse,
} from '@nadoutong/contracts';
import { platformApiRequest } from './client.ts';

/**
 * Typed wrappers around platform-api `/v1/usage*`.
 *
 * The time-range filters (`from` / `to`) are ISO-8601 strings;
 * platform-api silently ignores malformed values. The `days` param on
 * the hourly endpoint is a small integer (1..30) — the caller is
 * responsible for clamping.
 */

const BASE_PATH = '/v1/usage';

/**
 * The browser's IANA zone, sent as `tz` on every bucketed usage query (#1678).
 *
 * The usage window is built from the visitor's local clock, so the server has
 * to cut its buckets on the same calendar or requests either side of the UTC
 * day boundary land in the wrong bar. Falls back to `undefined` (server
 * default `UTC`) when the runtime cannot name a zone.
 */
export function browserTimeZone(): string | undefined {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return typeof zone === 'string' && zone.length > 0 ? zone : undefined;
}

export interface UsageRange {
  readonly from?: string;
  readonly to?: string;
}

export interface UsageRequestsQuery extends UsageRange {
  readonly requestId?: string;
  readonly apiKeyId?: string;
  readonly status?: 'success' | 'error';
  readonly limit?: number;
  readonly offset?: number;
  /**
   * Archive-detail hydration. Omitted = hydrate (server default). Only
   * an explicit `'summary'` skips OSS reads for archived rows.
   */
  readonly detail?: 'summary' | 'full';
}

export type AdminUsageDashboardClientQuery = AdminUsageDashboardQuery;

function buildQuery(query: UsageRequestsQuery | undefined): string {
  if (query === undefined) return '';
  const params = new URLSearchParams();
  if (typeof query.from === 'string' && query.from.length > 0) {
    params.set('from', query.from);
  }
  if (typeof query.to === 'string' && query.to.length > 0) {
    params.set('to', query.to);
  }
  if (typeof query.requestId === 'string' && query.requestId.length > 0) {
    params.set('requestId', query.requestId);
  }
  if (typeof query.apiKeyId === 'string' && query.apiKeyId.length > 0) {
    params.set('apiKeyId', query.apiKeyId);
  }
  if (query.status === 'success' || query.status === 'error') {
    params.set('status', query.status);
  }
  if (query.limit !== undefined) {
    params.set('limit', String(Math.max(1, Math.min(200, Math.floor(query.limit)))));
  }
  if (query.offset !== undefined) {
    params.set('offset', String(Math.max(0, Math.floor(query.offset))));
  }
  if (query.detail === 'summary' || query.detail === 'full') {
    params.set('detail', query.detail);
  }
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

function buildAdminDashboardQuery(query: AdminUsageDashboardClientQuery | undefined): string {
  if (query === undefined) return '';
  const params = new URLSearchParams();
  setQuery(params, 'from', query.from);
  setQuery(params, 'to', query.to);
  setQuery(params, 'granularity', query.granularity);
  setQuery(params, 'groupBy', query.groupBy);
  setQuery(params, 'model', query.model);
  // #1672: the model dimension is keyed by vendor + model. `vendor` only
  // narrows an already-chosen `model` — the server ignores it alone — so
  // it is sent as its own field and never folded into `model`.
  setQuery(params, 'vendor', query.vendor);
  setQuery(params, 'apiKeyId', query.apiKeyId);
  setQuery(params, 'memberAccountId', query.memberAccountId);
  setQuery(params, 'department', query.department);
  setQuery(params, 'q', query.q);
  // #1678: an explicit `tz` wins; otherwise the browser's own zone, so the
  // trend buckets are cut on the same calendar the window was built from.
  setQuery(params, 'tz', query.tz ?? browserTimeZone());
  if (query.limit !== undefined) {
    params.set('limit', String(Math.max(1, Math.min(100, Math.floor(query.limit)))));
  }
  if (query.offset !== undefined) {
    params.set('offset', String(Math.max(0, Math.floor(query.offset))));
  }
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

function setQuery(params: URLSearchParams, name: string, value: string | undefined): void {
  if (typeof value === 'string' && value.length > 0) {
    params.set(name, value);
  }
}

export async function getUsageSummary(
  fetchImpl: typeof fetch,
  range?: UsageRange,
  signal?: AbortSignal,
): Promise<UsageSummaryResponse> {
  return platformApiRequest<UsageSummaryResponse>(
    `${BASE_PATH}${buildQuery(range)}`,
    { signal },
    fetchImpl,
  );
}

export async function getUsageHourly(
  fetchImpl: typeof fetch,
  days: number,
  signal?: AbortSignal,
): Promise<UsageHourlyResponse> {
  const clamped = Math.max(1, Math.min(30, Math.floor(days)));
  const params = new URLSearchParams({ days: String(clamped) });
  return platformApiRequest<UsageHourlyResponse>(
    `${BASE_PATH}/hourly?${params.toString()}`,
    { signal },
    fetchImpl,
  );
}

export interface UsageDailyQuery {
  /** Rolling window ending now; ignored when `from`+`to` are both set. */
  readonly days?: number;
  readonly from?: string;
  readonly to?: string;
  /**
   * IANA zone the day buckets are cut on (#1678); mirrors the contract's
   * `UsageDailyQuery.tz`. Absent → the browser's own zone is sent, so the
   * bucket calendar matches the local-day window the date picker builds.
   */
  readonly tz?: string;
}

/**
 * An explicit `from`/`to` ISO window wins over `days` (custom date-range
 * filter); otherwise the rolling `days` form is sent, clamped to the
 * gateway-side cap (365) so a hand-crafted URL still renders cleanly.
 *
 * Either way the bucketing timezone rides along (#1678) so `days[].date`
 * labels are local calendar days rather than UTC ones.
 */
export async function getUsageDaily(
  fetchImpl: typeof fetch,
  query: UsageDailyQuery,
  signal?: AbortSignal,
): Promise<UsageDailyResponse> {
  const params = new URLSearchParams();
  const hasWindow =
    typeof query.from === 'string' &&
    query.from.length > 0 &&
    typeof query.to === 'string' &&
    query.to.length > 0;
  if (hasWindow && query.from !== undefined && query.to !== undefined) {
    params.set('from', query.from);
    params.set('to', query.to);
  } else {
    const clamped = Math.max(1, Math.min(365, Math.floor(query.days ?? 30)));
    params.set('days', String(clamped));
  }
  setQuery(params, 'tz', query.tz ?? browserTimeZone());
  return platformApiRequest<UsageDailyResponse>(
    `${BASE_PATH}/daily?${params.toString()}`,
    { signal },
    fetchImpl,
  );
}

export async function getUsageByKey(
  fetchImpl: typeof fetch,
  range?: UsageRange,
  signal?: AbortSignal,
): Promise<UsageByKeyResponse> {
  return platformApiRequest<UsageByKeyResponse>(
    `${BASE_PATH}/by-key${buildQuery(range)}`,
    { signal },
    fetchImpl,
  );
}

export async function getUsageRequests(
  fetchImpl: typeof fetch,
  query?: UsageRequestsQuery,
  signal?: AbortSignal,
): Promise<UsageRequestLogsResponse> {
  return platformApiRequest<UsageRequestLogsResponse>(
    `${BASE_PATH}/requests${buildQuery(query)}`,
    { signal },
    fetchImpl,
  );
}

export async function getAdminUsageDashboard(
  fetchImpl: typeof fetch,
  query?: AdminUsageDashboardClientQuery,
  signal?: AbortSignal,
): Promise<AdminUsageDashboardResponse> {
  return platformApiRequest<AdminUsageDashboardResponse>(
    `${BASE_PATH}/admin-dashboard${buildAdminDashboardQuery(query)}`,
    { signal },
    fetchImpl,
  );
}
